import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { FeedbackSupplier } from '@erp/rpc';

import { orpc } from '@/api/rpc/client';

/** Mutation errors go to a toast; nothing here depends on the component. */
function showError(error: Error): void {
  toast.error(error.message);
}

/**
 * One supplier's Renewal Rules. The list is fetched once for the org and
 * shared by every supplier's panel; unreadable rules have no readable
 * supplier, so they are reported with each.
 */
export function useRenewalRules(supplier: FeedbackSupplier) {
  const query = useQuery(
    orpc.pricing.renewalRules.list.queryOptions({ input: {} }),
  );
  return {
    rules: (query.data?.rules ?? []).filter(
      (rule) => rule.supplier === supplier,
    ),
    unreadableRuleIds: query.data?.unreadableRuleIds ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
  };
}

/** Create / update / delete for Renewal Rules, each refreshing the list. */
export function useRenewalRuleMutations() {
  const queryClient = useQueryClient();
  function refresh(): Promise<void> {
    return queryClient.invalidateQueries({
      queryKey: orpc.pricing.renewalRules.list.key(),
    });
  }
  const create = useMutation({
    ...orpc.pricing.renewalRules.create.mutationOptions(),
    onSuccess: refresh,
    onError: showError,
  });
  const update = useMutation({
    ...orpc.pricing.renewalRules.update.mutationOptions(),
    onSuccess: refresh,
    onError: showError,
  });
  const remove = useMutation({
    ...orpc.pricing.renewalRules.delete.mutationOptions(),
    onSuccess: refresh,
    onError: showError,
  });
  return { create, update, remove };
}
